import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { DEFAULT_COMMANDS, selectCommands } from "./commands";

// Cmd/Ctrl+K command palette.
//
// Lists commands from `./commands.js` (registry-driven). Subscribes to
// `netbase:openCommandPalette` so `useGlobalShortcuts` is the only owner of
// the keyboard shortcut. Availability is re-evaluated each time the palette
// opens (so e.g. signing in/out doesn't require a refresh).

function getSignedInState() {
  try {
    return Boolean(localStorage.getItem("sessioninfo"));
  } catch {
    return false;
  }
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSignedIn, setIsSignedIn] = useState(getSignedInState);
  const inputRef = useRef(null);

  useEffect(() => {
    const onOpen = () => {
      setOpen(true);
      setQuery("");
      setActiveIndex(0);
      setIsSignedIn(getSignedInState());
    };
    window.addEventListener("netbase:openCommandPalette", onOpen);
    return () => window.removeEventListener("netbase:openCommandPalette", onOpen);
  }, []);

  useEffect(() => {
    if (open) {
      // Defer focus so the dialog has mounted its content.
      const handle = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(handle);
    }
    return undefined;
  }, [open]);

  const ctx = useMemo(
    () => ({
      navigate,
      isSignedIn,
      pathname: location.pathname || ""
    }),
    [navigate, isSignedIn, location.pathname]
  );

  const results = useMemo(
    () => selectCommands(DEFAULT_COMMANDS, ctx, query),
    [ctx, query]
  );

  const handleClose = useCallback(() => setOpen(false), []);

  const handleRun = useCallback(
    (command) => {
      if (!command || typeof command.run !== "function") return;
      handleClose();
      try {
        command.run(ctx);
      } catch (error) {
        // Don't trap the user inside the palette if a command throws.
        console.error("CommandPalette: command failed", command.id, error);
      }
    },
    [ctx, handleClose]
  );

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((current) => Math.min(current + 1, Math.max(results.length - 1, 0)));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((current) => Math.max(current - 1, 0));
      } else if (event.key === "Enter") {
        event.preventDefault();
        handleRun(results[activeIndex]);
      } else if (event.key === "Escape") {
        handleClose();
      }
    },
    [activeIndex, handleClose, handleRun, results],
  );

  // Keep the highlight in bounds when the filtered list shrinks.
  useEffect(() => {
    if (activeIndex >= results.length) {
      setActiveIndex(Math.max(results.length - 1, 0));
    }
  }, [activeIndex, results.length]);

  // Group results by command.group while preserving the order returned by
  // selectCommands. Each entry tracks its absolute index so keyboard
  // navigation lines up with the rendered list.
  const grouped = useMemo(() => {
    const groups = [];
    const indexOf = new Map();
    results.forEach((command, index) => {
      indexOf.set(command.id, index);
      const last = groups[groups.length - 1];
      if (last && last.group === command.group) {
        last.commands.push({ ...command, absoluteIndex: index });
      } else {
        groups.push({ group: command.group, commands: [{ ...command, absoluteIndex: index }] });
      }
    });
    return { groups, indexOf };
  }, [results]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, overflow: "hidden" } }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
        <SearchRoundedIcon sx={{ color: "text.secondary" }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Type a command or jump to a page\u2026"
          sx={{ flex: 1, fontSize: 15 }}
          inputProps={{ "aria-label": "Command palette search" }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "none" }}>
          Esc to close
        </Typography>
      </Stack>
      <DialogContent sx={{ p: 0, maxHeight: "min(60vh, 480px)", overflow: "auto" }}>
        {results.length === 0 ? (
          <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No matches. Try a different search.
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ py: 0.5 }}>
            {grouped.groups.map((groupItem, groupIndex) => (
              <Box key={groupItem.group}>
                {groupIndex > 0 ? <Divider sx={{ my: 0.5 }} /> : null}
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    px: 2,
                    pt: 0.75,
                    pb: 0.25,
                    color: "text.secondary",
                    letterSpacing: 0.6,
                    fontSize: 10
                  }}
                >
                  {groupItem.group}
                </Typography>
                {groupItem.commands.map((command) => {
                  const active = command.absoluteIndex === activeIndex;
                  return (
                    <ListItem key={command.id} disablePadding>
                      <ListItemButton
                        selected={active}
                        onMouseEnter={() => setActiveIndex(command.absoluteIndex)}
                        onClick={() => handleRun(command)}
                        sx={{ px: 2 }}
                      >
                        <ListItemText
                          primary={command.label}
                          primaryTypographyProps={{ sx: { fontWeight: active ? 700 : 500 } }}
                        />
                        {command.hint ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
                              fontSize: 11,
                              mr: 1
                            }}
                          >
                            {command.hint}
                          </Typography>
                        ) : null}
                        <ArrowForwardRoundedIcon fontSize="small" sx={{ opacity: active ? 1 : 0.3 }} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
